import { EventEmitter } from './event-emitter';
import { type IFilter, A9DataProviderHelper } from '../data-provider';
import { uniqArray } from '../helpers';

/**
 * @event Selection#update
 */

export type TUpdateState = {
    id: number;
    size: number;
};

type ChangeResponse<ObjectResponse = object> = {
    id: number;
    inverted: boolean;
    name: string;
    notes: string;
    objects: Array<ObjectResponse>;
};

export abstract class AbstractSelection<T> extends EventEmitter {
    private _queuedCalls: any[] = [];
    private _queuedInProgress: boolean = false;
    private _isDisabled: boolean = false;

    protected _id: number;
    protected _size: number = 0;
    protected _idField = 'id';

    protected active: boolean = false;

    constructor(id = 0) {
        super();
        this._id = id;
    }

    public abstract update(): Promise<void>;

    public abstract isSelected(item: T): boolean;

    public abstract setSelection(item: T, value: boolean): void;

    public abstract getSelectedItems(): Promise<number[]>;

    public get id(): number {
        return this._id;
    }

    public get size(): number {
        return this._size;
    }

    public get loading(): boolean {
        return this._queuedInProgress;
    }

    public get idField() {
        return this._idField;
    }

    public set idField(name: string) {
        this._idField = name;
    }

    public get disabled() {
        return this._isDisabled;
    }

    public set disabled(value: boolean) {
        this._isDisabled = value;
    }

    public get single() {
        return false;
    }

    public get global() {
        return false;
    }

    public isSelectable(_item: T) {
        return true;
    }

    /**
     * Selects the items in the grid
     * @param items
     * @returns
     */
    public select(items: T[]): Promise<void> {
        return this._queue(() => this._modify(items, []));
    }

    /**
     * Deselects the items in the grid
     * @param items
     * @returns
     */
    public deselect(items: T[]): Promise<void> {
        return this._queue(() => this._modify([], items));
    }

    /**
     * Inverts the selection of the items in the grid
     * @param items
     * @returns
     */
    public invert(items: T[]): Promise<void> {
        const toBeSelected = items.filter(item => !this.isSelected(item));
        const toBeDeselected = items.filter(item => this.isSelected(item));

        return this._queue(() => this._modify(uniqArray<T>(toBeSelected, this._idField), uniqArray<T>(toBeDeselected, this._idField)));
    }

    /**
     * Selects all of the items in the grid
     * @param filter
     * @returns
     */
    public selectAll(filter: IFilter | null = null): Promise<void> {
        return this._queue(() => this._selectAll(filter));
    }

    /**
     * Unselects all of the items on the grid
     * @returns
     */
    public deselectAll(): Promise<void> {
        return this._queue(() => this._deselectAll());
    }

    /**
     * Inverts the selection of all items in the grid
     * @param filter
     * @returns
     */
    public invertAll(filter: IFilter | null = null): Promise<void> {
        return this._queue(() => this._invertAll(filter));
    }

    /**
     * Removes the selection
     * @returns
     */
    public remove(): Promise<void> {
        return this._queue(() => this._remove());
    }

    /**
     * Toggles the selection of the item (selects if it is not selected, deselects if it is selected)
     * @param item
     * @returns
     */
    public toggle(item: T): Promise<void> {
        this.setSelection(item, !this.isSelected(item));
        if (this.isSelected(item)) {
            return this.select([item]);
        } else {
            return this.deselect([item]);
        }
    }

    public activate(flag: boolean): void {
        this.active = flag;
        this.emit('activate', this.active);
    }

    public isActive(): boolean {
        return this.active;
    }

    public waitForLoaded() {
        return this._queue(() => {
            return Promise.resolve();
        });
    }

    public get UPDATE(): 'update' {
        return 'update';
    }

    public get REMOVE(): 'remove' {
        return 'remove';
    }

    protected abstract _modify(selectItems: T[], deselectItems: T[]): Promise<void>;

    protected abstract _selectAll(filter: IFilter | null): Promise<void>;

    protected abstract _deselectAll(): Promise<void>;

    protected abstract _invertAll(filter: IFilter | null): Promise<void>;

    protected abstract _remove(): Promise<void>;

    protected _updateState(id: number, size: number) {
        this._id = id;
        this._size = size;

        this.emit(this.UPDATE, <TUpdateState>{
            id: this._id,
            size: this._size
        });
    }

    protected _queue(call: () => Promise<any>): Promise<any> {
        this.emit('update:before');
        return new Promise((resolve, reject) => {
            const wrap = () => {
                this._queuedInProgress = true;
                call()
                    .then(response => {
                        resolve(response);
                        this._next();
                    })
                    .catch(error => {
                        reject(error);
                        this._next();
                    });
            };
            if (this._queuedInProgress) {
                this._queuedCalls.push(wrap);
            } else {
                wrap();
            }
        });
    }

    private _next() {
        const next = this._queuedCalls.shift();
        if (next) {
            next();
        } else {
            this.emit('update:after');
            this._queuedInProgress = false;
        }
    }
}

/* for selection apis who are dont support /filter method */
export abstract class AbstractDataSelection<T> extends AbstractSelection<T> {
    protected path: string = '';
    protected objectIdFieldName: string = '';
    protected $http: angular.IHttpService;

    constructor($injector: angular.auto.IInjectorService, id: any = 0, params: any = null) {
        super(id);
        this.$http = $injector.get('$http');
        this.init(params);
        this.update();
    }

    protected abstract init(params?: any): void;

    public waitForLoaded(): Promise<void> {
        return this._queue(() => {
            return Promise.resolve();
        });
    }

    public getAll(): Promise<any> {
        return this.getDetails(this.id);
    }

    public getInverted(): Promise<any> {
        return this._createInvertedSelection();
    }

    protected getDetails(id: any): Promise<any> {
        return <Promise<any>>this.$http
            .get(`${this.path}/${id}/details`)
            .then(response => {
                return this.checkValidateResponseData(response.data);
                // return response.data;
            })
            .catch(() => {
                return this.createEmptySelection().then(() => {
                    return this.$http.get(`${this.path}/${this._id}/details`).then(response => {
                        return this.checkValidateResponseData(response.data);
                        // return response.data;
                    });
                });
            });
    }

    public update(): Promise<void> {
        if (!this._id) {
            this._size = 0;
            return Promise.resolve();
        }
        return this._queue(() => {
            return this.getDetails(this._id).then(details => {
                this._updateState(details.id, details.objects.length);
            });
        });
    }

    public isSelected(item: any) {
        return !!item.selected;
    }

    public get global(): boolean {
        return true;
    }

    public setSelection(item: any, value: any): void {
        item.selected = value;
    }

    public async getSelectedItems(): Promise<number[]> {
        return this.getDetails(this._id).then(details => details.objects.map(object => object[this.objectIdFieldName]));
    }

    protected _modify(select: any[], deselect: any[]): Promise<void> {
        return this.getSelectionId().then((id: any) => {
            let excludes: any[] = [];
            let includes: any[] = [];

            if (select.length > 0) {
                includes = select.map(item => item[this._idField]).filter(item => item);
            }

            if (deselect.length > 0) {
                excludes = deselect.map(item => item[this._idField]).filter(item => item);
            }

            const payload = {
                excludes: excludes,
                includes: includes
            };

            return this.$http.put<ChangeResponse>(`${this.path}/${id}/change`, payload).then(response => {
                const details = this.checkValidateResponseData(response.data);
                this._updateState(details.id, details.objects.length);
            });
        });
    }

    protected checkValidateResponseData<Data>(data: Data) {
        return data;
    }

    protected _selectAll(filter: IFilter | null): Promise<void> {
        return this._remove().then(() => {
            return this.createFilterBasedSelection(filter).then(() => {
                return this.$http
                    .put(`${this.path}/${this._id}/inverted`, {
                        inverted: true
                    })
                    .then(() => {
                        //	console.log('update state', this._id);
                    });
            });
        });
    }

    protected _deselectAll() {
        return this._remove();
    }

    protected _invertAll(filter: IFilter) {
        /*
         create new selectAll and exclude prev selected items
         */
        let chain = Promise.resolve();
        const itemsToDeselect: unknown[] = [];
        if (this.id) {
            chain = chain.then(() => {
                return this.getDetails(this.id).then(data => {
                    data.objects.forEach(object =>
                        itemsToDeselect.push({
                            id: object[this.objectIdFieldName]
                        })
                    );
                });
            });
        }

        chain = chain
            .then(() => {
                // new selectAll
                return this._remove().then(() => this.createFilterBasedSelection(filter));
            })
            .then(() => {
                // exclude prev selected items
                // don't hide modify request under length check because output of modify is used for stats
                return this._modify([], itemsToDeselect);
            });
        return chain;
    }

    private _createInvertedSelection() {
        return <Promise<void>>this.$http
            .put<{ id: number; objects: unknown[] }>(`${this.path}/${this._id}/inverted`, {
                name: '',
                inverted: true
            })
            .then(response => {
                const details = this.checkValidateResponseData(response.data);
                this._updateState(details.id, details.objects.length);
            });
    }

    protected _remove(): Promise<void> {
        if (this.id) {
            this.emit(this.REMOVE);
            return <Promise<void>>this.$http.delete<void>(`${this.path}/${this.id}`).then(() => {
                delete this._id;
                this._size = 0;
                this._updateState(0, 0);
            });
        } else {
            return Promise.resolve();
        }
    }

    private async getSelectionId() {
        if (this._id) {
            return Promise.resolve(this._id);
        } else {
            return this.createEmptySelection().then(() => this._id);
        }
    }

    protected async createEmptySelection() {
        return this.$http.post<{ id: number }>(`${this.path}`, { name: '' }).then(response => {
            const details = this.checkValidateResponseData(response.data);
            this._updateState(details.id, 0);
        });
    }

    // TODO doesn't word with filters for a while
    protected createFilterBasedSelection(_filter: IFilter) {
        const params = {};
        //		const helper = new A9DataProviderHelper();
        // TODO need to realize filters on the selection
        //helper.addFilterParameters(params, filter);
        // return this.$http.post('middle://rs/selection/customers/filter', params).then(response => {
        //     let selection = <ITournamentPlayersSelection>response.data;
        //     return selection.id;
        // }).then(id => {
        //     return this.getDetails(id);
        // }).then(selection => {
        //     this._updateState(selection.id, selection.objects.length);
        // });
        return this.$http
            .post<{ id: unknown }>(`${this.path}`, params)
            .then(response => {
                const selection = this.checkValidateResponseData(response.data);
                return selection.id;
            })
            .then(id => {
                return this.getDetails(id);
            })
            .then(selection => {
                this._updateState(selection.id, selection.objects.length);
            });
    }
}

/* for selection apis with /filter method */
export abstract class AbstractFilterDataSelection<T> extends AbstractDataSelection<T> {
    protected createFilterBasedSelection(filter: IFilter): angular.IPromise<void> {
        const params: object = {};
        const helper = new A9DataProviderHelper();
        helper.addFilterParameters(params, filter);
        return this.$http
            .post<{ id: number; objects: unknown[] }>(`${this.path}/filter`, params)
            .then(response => {
                const selection = this.checkValidateResponseData(response.data);
                return selection.id;
            })
            .then((id: number) => {
                return this.getDetails(id);
            })
            .then(selection => {
                this._updateState(selection.id, selection.objects.length);
            });
    }

    // protected static checkValidateResponseData(data) {
    //     return data;
    // }

    protected _selectAll(filter: IFilter): Promise<void> {
        return this._remove().then(() => {
            return this.createFilterBasedSelection(filter).then(() => {
                return;
            });
        });
    }
}
